import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;

/** 2回叩いたときの倍率 */
const TAP_SCALE = 2.5;

/** 2回目と認める間隔 */
const DOUBLE_TAP_MS = 300;

/** 叩いたのか動かしたのかの境目 */
const TAP_SLOP_PX = 10;

interface Point {
  x: number;
  y: number;
}

/** 倍率と寄せ幅は必ず揃って変わるので、1つの状態として持つ */
interface View {
  scale: number;
  offset: Point;
}

const RESET: View = { scale: MIN_SCALE, offset: { x: 0, y: 0 } };

interface Props {
  src: string;
  /** 原本が届くまで下敷きに出すサムネイル。荒くても黒画面よりずっといい */
  placeholder?: string;
  /** 等倍でなくなったら知らせる。呼び出し側は送り(スワイプ)を止める */
  onZoomedChange: (zoomed: boolean) => void;
  /** 1回だけ叩いたとき。2回目を待ってから呼ぶので、拡大と取り違えない */
  onTap?: () => void;
  onError: () => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function distanceOf(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * ピンチと車輪で拡大できる画像。
 *
 * ライブラリを足さずに手で書いている。要るのは1枚を拡大して見ることだけで、
 * そのために数十KBを全員に配るのは割に合わない。
 */
export default function ZoomableImage({ src, placeholder, onZoomedChange, onTap, onError }: Props) {
  const box = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<View>(RESET);
  const [loaded, setLoaded] = useState(false);

  // 触れている指(またはマウス)の現在地
  const pointers = useRef(new Map<number, Point>());
  const pinch = useRef<{ distance: number; scale: number } | null>(null);
  const pan = useRef<{ from: Point; offset: Point } | null>(null);
  const tappedAt = useRef(0);
  const singleTapTimer = useRef(0);

  // 予約したシングルタップが、閉じた後に発火しないようにする
  useEffect(() => {
    return () => {
      clearTimeout(singleTapTimer.current);
    };
  }, []);

  /** 器の中心を原点とした座標に直す */
  const localPoint = useCallback((clientX: number, clientY: number): Point => {
    const rect = box.current?.getBoundingClientRect();
    if (rect === undefined) return { x: 0, y: 0 };
    return { x: clientX - (rect.left + rect.width / 2), y: clientY - (rect.top + rect.height / 2) };
  }, []);

  /** 拡大しても画像が画面の外へ逃げないように寄せ幅を抑える */
  const clampOffset = useCallback((next: Point, atScale: number): Point => {
    const rect = box.current?.getBoundingClientRect();
    if (rect === undefined) return next;
    const limitX = ((atScale - 1) * rect.width) / 2;
    const limitY = ((atScale - 1) * rect.height) / 2;
    return { x: clamp(next.x, -limitX, limitX), y: clamp(next.y, -limitY, limitY) };
  }, []);

  /**
   * 指(またはカーソル)の下にある一点を動かさずに倍率を変える。
   * 中心に向かって拡大すると、見たい場所が指から逃げていく。
   */
  const zoomAround = useCallback(
    (target: number, at: Point) => {
      setView((current) => {
        const scale = clamp(target, MIN_SCALE, MAX_SCALE);
        if (scale === MIN_SCALE) return RESET;
        const ratio = scale / current.scale;
        return {
          scale,
          offset: clampOffset(
            {
              x: at.x - (at.x - current.offset.x) * ratio,
              y: at.y - (at.y - current.offset.y) * ratio,
            },
            scale,
          ),
        };
      });
    },
    [clampOffset],
  );

  // 別のメディアに移ったら等倍に戻す。前の拡大が残っていると何を見ているか分からない
  useEffect(() => {
    setView(RESET);
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    onZoomedChange(view.scale > MIN_SCALE);
  }, [view.scale, onZoomedChange]);

  // React の onWheel は passive で付くことがあり preventDefault が効かない。
  // ページ側の拡大を止める必要があるので、自前で非 passive の listener を張る
  useEffect(() => {
    const node = box.current;
    if (node === null) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setView((current) => {
        const scale = clamp(current.scale * Math.exp(-event.deltaY / 300), MIN_SCALE, MAX_SCALE);
        if (scale === MIN_SCALE) return RESET;
        const at = localPoint(event.clientX, event.clientY);
        const ratio = scale / current.scale;
        return {
          scale,
          offset: clampOffset(
            {
              x: at.x - (at.x - current.offset.x) * ratio,
              y: at.y - (at.y - current.offset.y) * ratio,
            },
            scale,
          ),
        };
      });
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", onWheel);
    };
  }, [localPoint, clampOffset]);

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);

    const [first, second] = [...pointers.current.values()];
    if (first !== undefined && second !== undefined) {
      pinch.current = { distance: distanceOf(first, second), scale: view.scale };
      pan.current = null;
      return;
    }
    pan.current = { from: { x: event.clientX, y: event.clientY }, offset: view.offset };
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const points = [...pointers.current.values()];
    const [first, second] = points;

    const pinching = pinch.current;
    if (pinching !== null && first !== undefined && second !== undefined) {
      const ratio = distanceOf(first, second) / pinching.distance;
      const middle = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
      zoomAround(pinching.scale * ratio, localPoint(middle.x, middle.y));
      return;
    }

    // 等倍のときは動かさない。1本指の横移動は送り(スワイプ)に譲る
    const panning = pan.current;
    if (panning !== null && points.length === 1 && view.scale > MIN_SCALE) {
      setView((current) => ({
        scale: current.scale,
        offset: clampOffset(
          {
            x: panning.offset.x + (event.clientX - panning.from.x),
            y: panning.offset.y + (event.clientY - panning.from.y),
          },
          current.scale,
        ),
      }));
    }
  }

  function onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const started = pan.current;
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) pan.current = null;

    // 指が残っている、または動かして離したなら「叩いた」ではない
    const point = { x: event.clientX, y: event.clientY };
    const moved = started !== null && distanceOf(started.from, point) > TAP_SLOP_PX;
    if (moved || pointers.current.size > 0) return;

    const now = Date.now();
    if (now - tappedAt.current < DOUBLE_TAP_MS) {
      tappedAt.current = 0;
      clearTimeout(singleTapTimer.current);
      if (view.scale > MIN_SCALE) setView(RESET);
      else zoomAround(TAP_SCALE, localPoint(point.x, point.y));
      return;
    }
    tappedAt.current = now;
    // 2回目が来なければシングルタップと確定する
    clearTimeout(singleTapTimer.current);
    singleTapTimer.current = window.setTimeout(() => {
      onTap?.();
    }, DOUBLE_TAP_MS);
  }

  const zoomed = view.scale > MIN_SCALE;

  return (
    <div
      ref={box}
      className="relative flex size-full items-center justify-center overflow-hidden"
      // ブラウザ自身の拡大や引っ張りを止め、ポインタの動きを全部こちらで受ける
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {placeholder !== undefined && !loaded && (
        <img
          src={placeholder}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
      {/* transform が stacking context を作るため、この img は常に下敷きの上に描かれる */}
      <img
        src={src}
        alt=""
        draggable={false}
        onLoad={() => {
          setLoaded(true);
        }}
        onError={onError}
        className="max-h-full max-w-full object-contain select-none"
        style={{
          transform: `translate(${String(view.offset.x)}px, ${String(view.offset.y)}px) scale(${String(view.scale)})`,
          cursor: zoomed ? "grab" : "zoom-in",
        }}
      />
    </div>
  );
}
